import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  Banknote,
  Settings,
  CheckCircle2,
  XCircle,
  Clock,
  Search,
  Filter,
  Building2,
  User,
  Mail,
  Calendar,
  DollarSign,
  Send,
  AlertCircle,
  Copy,
  ChevronDown,
  RefreshCw
} from 'lucide-react';
import Pagination from '../components/Pagination';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/*
 * Altam™ Operating Services
 * Online Payments Management (e-Transfer)
 * Copyright (c) 2026 Pecaro Consulting Inc. All rights reserved.
 */

export default function OnlinePayments() {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('requests');
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [etransferSettings, setEtransferSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  
  // Settings form
  const [settingsForm, setSettingsForm] = useState({
    etransfer_email: '',
    etransfer_recipient_name: '',
    autodeposit_enabled: true,
    instructions: ''
  });
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Confirm payment modal
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [confirmNotes, setConfirmNotes] = useState('');
  const [processingAction, setProcessingAction] = useState(false);

  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    if (selectedProperty) {
      fetchEtransferSettings();
    }
  }, [selectedProperty]);

  useEffect(() => {
    fetchPaymentRequests();
  }, [selectedProperty, statusFilter, currentPage]);

  const fetchProperties = async () => {
    try {
      const response = await axios.get(`${API}/corporations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProperties(response.data);
      if (response.data.length > 0) {
        setSelectedProperty(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch properties:', error);
    }
  };

  const fetchEtransferSettings = async () => {
    try {
      const response = await axios.get(`${API}/payments/etransfer-settings/${selectedProperty}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setEtransferSettings(response.data);
        setSettingsForm({
          etransfer_email: response.data.etransfer_email || '',
          etransfer_recipient_name: response.data.etransfer_recipient_name || '',
          autodeposit_enabled: response.data.autodeposit_enabled ?? true,
          instructions: response.data.instructions || ''
        });
      } else {
        setEtransferSettings(null);
        setSettingsForm({
          etransfer_email: '',
          etransfer_recipient_name: '',
          autodeposit_enabled: true,
          instructions: ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch e-Transfer settings:', error);
      setEtransferSettings(null);
    }
  };

  const fetchPaymentRequests = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: 20
      });
      if (selectedProperty) params.append('entity_id', selectedProperty);
      if (statusFilter) params.append('status', statusFilter);

      const response = await axios.get(`${API}/payments/requests?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentRequests(response.data.data);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to fetch payment requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!settingsForm.etransfer_email || !settingsForm.etransfer_recipient_name) {
      alert('Please fill in all required fields');
      return;
    }

    setSavingSettings(true);
    try {
      await axios.post(
        `${API}/payments/etransfer-settings`,
        {
          entity_id: selectedProperty,
          ...settingsForm
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('e-Transfer settings saved successfully!');
      fetchEtransferSettings();
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('Failed to save settings. Please try again.');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!referenceNumber.trim()) {
      alert('Please enter the e-Transfer reference number');
      return;
    }

    setProcessingAction(true);
    try {
      await axios.post(
        `${API}/payments/requests/${selectedRequest.id}/confirm`,
        {
          payment_request_id: selectedRequest.id,
          reference_number: referenceNumber,
          notes: confirmNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Payment confirmed successfully!');
      setShowConfirmModal(false);
      setSelectedRequest(null);
      setReferenceNumber('');
      setConfirmNotes('');
      fetchPaymentRequests();
    } catch (error) {
      console.error('Failed to confirm payment:', error);
      alert('Failed to confirm payment. Please try again.');
    } finally {
      setProcessingAction(false);
    }
  };

  const handleRejectPayment = async (requestId) => {
    const reason = prompt('Enter rejection reason (optional):');
    
    setProcessingAction(true);
    try {
      await axios.post(
        `${API}/payments/requests/${requestId}/reject?reason=${encodeURIComponent(reason || 'Payment not received')}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Payment request rejected');
      fetchPaymentRequests();
    } catch (error) {
      console.error('Failed to reject payment:', error);
      alert('Failed to reject payment request');
    } finally {
      setProcessingAction(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700',
      confirmed: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-rose-100 text-rose-700'
    };
    return styles[status] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="space-y-6" data-testid="online-payments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Online Payments</h1>
          <p className="text-slate-500">Manage e-Transfer settings and payment requests</p>
        </div>
        
        {/* Property Filter */}
        <div className="flex items-center gap-3">
          <Building2 className="w-5 h-5 text-slate-400" />
          <select
            value={selectedProperty}
            onChange={(e) => {
              setSelectedProperty(e.target.value);
              setCurrentPage(1);
            }}
            className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            {properties.map((prop) => (
              <option key={prop.id} value={prop.id}>{prop.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex gap-6">
          <button
            onClick={() => setActiveTab('requests')}
            className={`pb-3 px-1 border-b-2 font-medium transition-colors ${
              activeTab === 'requests'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <Banknote className="w-4 h-4" />
              Payment Requests
            </div>
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`pb-3 px-1 border-b-2 font-medium transition-colors ${
              activeTab === 'settings'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <div className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              e-Transfer Settings
            </div>
          </button>
        </nav>
      </div>

      {/* Payment Requests Tab */}
      {activeTab === 'requests' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
            <button
              onClick={fetchPaymentRequests}
              className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="bg-amber-100 p-2 rounded-lg">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">
                    {paymentRequests.filter(r => r.status === 'pending').length}
                  </p>
                  <p className="text-sm text-amber-700">Pending Review</p>
                </div>
              </div>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="bg-emerald-100 p-2 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-600">
                    {paymentRequests.filter(r => r.status === 'confirmed').length}
                  </p>
                  <p className="text-sm text-emerald-700">Confirmed</p>
                </div>
              </div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className="bg-slate-100 p-2 rounded-lg">
                  <DollarSign className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-700">
                    {formatCurrency(
                      paymentRequests
                        .filter(r => r.status === 'confirmed')
                        .reduce((sum, r) => sum + r.amount, 0)
                    )}
                  </p>
                  <p className="text-sm text-slate-500">Total Confirmed</p>
                </div>
              </div>
            </div>
          </div>

          {/* Requests Table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : paymentRequests.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-slate-500">
                <Banknote className="w-12 h-12 text-slate-300 mb-3" />
                <p>No payment requests found</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Tenant</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Unit</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Amount</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Type</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Date</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Status</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentRequests.map((request) => (
                    <tr key={request.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-slate-900">{request.user_name}</p>
                          <p className="text-xs text-slate-500">{request.user_email}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-slate-700">{request.unit_number}</td>
                      <td className="py-3 px-4 font-semibold text-slate-900">
                        {formatCurrency(request.amount)}
                      </td>
                      <td className="py-3 px-4 text-slate-600 capitalize">
                        {request.payment_type?.replace('_', ' ')}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-500">
                        {formatDate(request.created_at)}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(request.status)}`}>
                          {request.status}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {request.status === 'pending' && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                setSelectedRequest(request);
                                setShowConfirmModal(true);
                              }}
                              className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                              title="Confirm Payment"
                            >
                              <CheckCircle2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleRejectPayment(request.id)}
                              className="p-2 text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                              title="Reject"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                        {request.status === 'confirmed' && request.reference_number && (
                          <span className="text-xs text-slate-500">Ref: {request.reference_number}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={pagination.pages}
              totalItems={pagination.total}
              itemsPerPage={20}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="max-w-2xl">
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-emerald-100 p-2 rounded-lg">
                <Mail className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Interac e-Transfer Settings</h2>
                <p className="text-sm text-slate-500">
                  Configure e-Transfer payment details for {properties.find(p => p.id === selectedProperty)?.name}
                </p>
              </div>
            </div>

            {!etransferSettings && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">e-Transfer not configured</p>
                  <p className="text-sm text-amber-600">
                    Fill in the details below to enable online rent payments for tenants.
                  </p>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  e-Transfer Email <span className="text-rose-500">*</span>
                </label>
                <input
                  type="email"
                  value={settingsForm.etransfer_email}
                  onChange={(e) => setSettingsForm({ ...settingsForm, etransfer_email: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="payments@yourcompany.com"
                />
                <p className="text-xs text-slate-500 mt-1">
                  This email should be registered with Interac e-Transfer
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Recipient Name <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={settingsForm.etransfer_recipient_name}
                  onChange={(e) => setSettingsForm({ ...settingsForm, etransfer_recipient_name: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Property Management Company Inc."
                />
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="autodeposit"
                  checked={settingsForm.autodeposit_enabled}
                  onChange={(e) => setSettingsForm({ ...settingsForm, autodeposit_enabled: e.target.checked })}
                  className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                />
                <label htmlFor="autodeposit" className="text-sm text-slate-700">
                  Auto-deposit enabled (tenants won't need a security question)
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Payment Instructions
                </label>
                <textarea
                  value={settingsForm.instructions}
                  onChange={(e) => setSettingsForm({ ...settingsForm, instructions: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  rows={3}
                  placeholder="Please include your unit number and name in the message field."
                />
                <p className="text-xs text-slate-500 mt-1">
                  Instructions shown to tenants when making a payment
                </p>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-200">
              <button
                onClick={handleSaveSettings}
                disabled={savingSettings}
                className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {savingSettings ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    Save Settings
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Payment Modal */}
      {showConfirmModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-4">Confirm e-Transfer Payment</h2>
            
            <div className="bg-slate-50 rounded-lg p-4 mb-4">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Tenant:</span>
                  <span className="font-medium text-slate-900">{selectedRequest.user_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Unit:</span>
                  <span className="font-medium text-slate-900">{selectedRequest.unit_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Amount:</span>
                  <span className="font-bold text-emerald-600">{formatCurrency(selectedRequest.amount)}</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  e-Transfer Reference Number <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={referenceNumber}
                  onChange={(e) => setReferenceNumber(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="e.g., CAxxxxxxxxx"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Notes (optional)
                </label>
                <textarea
                  value={confirmNotes}
                  onChange={(e) => setConfirmNotes(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  rows={2}
                  placeholder="Any additional notes..."
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setSelectedRequest(null);
                  setReferenceNumber('');
                  setConfirmNotes('');
                }}
                className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmPayment}
                disabled={processingAction || !referenceNumber}
                className="flex-1 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {processingAction ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                ) : (
                  <CheckCircle2 className="w-4 h-4" />
                )}
                Confirm Payment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
